import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  InputAdornment,
  Grid,
  Typography,
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
  Box,
  RadioGroup,
  Radio,
  Skeleton,
  Divider,
  Paper,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import * as consts from '@utilities/consts';
import {
  outputCentsToDollars,
  inputDollarsToCents,
  decimalFormatter,
  getMatchCount,
} from '@utilities/tools';
import { Initiative, type WYRQuestion, type Match } from '@types';
import { getWYRQuestion, getInitiativesByTags } from '@utilities/actions';
import { DonationDialog, CharityCard } from '@components';
import { ImpactDonut } from '@components';

export interface WYRDialogProps {
  onClose: () => void;
}

export function WYRDialog({ onClose }: WYRDialogProps): React.JSX.Element {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [busy, setBusy] = useState<boolean>(true);
  const [questionIndex, setQuestionIndex] = useState<number>(-1);
  const [questionMax, setQuestionMax] = useState<number>(3);
  const [questions, setQuestions] = useState<WYRQuestion[]>([]);
  const [answers, setAnswers] = useState<string[]>([]);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      await handleGetNextQuestion(questionMax, questionIndex, questions, answers);
    };
    fetchData();
  }, []);

  const handleGetRecommendations = async () => {
    const tags = questions.slice(-3).flatMap((question, index) => {
      return answers[index] === 'A' ? question.answerATags : question.answerBTags;
    });
    console.log(tags);
    let initiativeResponse = await getInitiativesByTags(tags);
    setInitiatives(initiativeResponse);
  };

  const handleGetNextQuestion = async (
    limit: number,
    currentQuestionIndex: number,
    currentQuestions: WYRQuestion[],
    currentAnswers: string[],
  ) => {
    setBusy(true);
    if (currentQuestionIndex + 1 < limit) {
      const existingFollowUpIds: Set<string> = new Set(
        currentQuestions.map((question) => question.id),
      );
      const answerFollowUpIds: string[] =
        currentAnswers[currentQuestionIndex] == 'A'
          ? currentQuestions[currentQuestionIndex].answerAFollowUpIds
          : currentAnswers[currentQuestionIndex] == 'B'
            ? currentQuestions[currentQuestionIndex].answerBFollowUpIds
            : [];
      const filteredFollowUpIds = answerFollowUpIds.filter((id) => !existingFollowUpIds.has(id));
      let followUpId =
        filteredFollowUpIds.length > 0
          ? filteredFollowUpIds[Math.floor(Math.random() * filteredFollowUpIds.length)]
          : '-1';

      let wyrQuestionResponse: WYRQuestion = await getWYRQuestion(followUpId);
      let limiter = 0;
      while (limiter < 3 && existingFollowUpIds.has(wyrQuestionResponse.id)) {
        wyrQuestionResponse = await getWYRQuestion(followUpId);
        limiter++;
      }
      if (existingFollowUpIds.has(wyrQuestionResponse.id)) {
        await handleGetRecommendations();
      } else {
        setQuestions([...currentQuestions, wyrQuestionResponse]);
        setAnswers([...currentAnswers, '']);
        setQuestionIndex(currentQuestionIndex + 1);
      }
    } else {
      await handleGetRecommendations();
    }
    setBusy(false);
  };

  const navigateToInitiative = (initiative: Initiative) => {
    window.location.href = `${consts.URL_INITIATIVE}?${consts.QUERYPARAMETER_INITIATIVEID}=${initiative.id}`;
  };

  const renderInitiative = (initiative: Initiative) => {
    return (
      <Grid
        container
        size={{ xs: 12 }}
        sx={{
          '& img': { boxShadow: 3 },
          '&:hover': {
            '& img': { boxShadow: 8 },
            '& h3': { color: '#2CAEC9' },
          },
          paddingTop: '24px',
          paddingBottom: '24px',
        }}
        spacing={1}
        alignItems="flex-start"
      >
        <Grid
          size={{ xs: 12, sm: 5 }}
          sx={{ paddingRight: '24px' }}
          direction="column"
          alignItems="flex-start"
          rowSpacing={2}
        >
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              borderRadius: '24px',
              cursor: 'pointer',
            }}
            onClick={() => {
              navigateToInitiative(initiative);
            }}
          >
            <img
              src={
                initiative.horizontalImageUrl
                  ? initiative.horizontalImageUrl
                  : consts.DEFAULT_INITIATIVEHORIZONTALIMAGEURL
              }
              alt="Category"
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '24px',
                objectFit: 'cover',
              }}
            />
          </Box>
        </Grid>
        <Grid
          size={{ xs: 12, sm: 7 }}
          container
          spacing={2}
          direction="column"
          alignItems="flex-start"
          rowSpacing={2}
        >
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="h3"
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                navigateToInitiative(initiative);
              }}
            >
              {initiative.title}
            </Typography>
            <Typography variant="body1" component="div" sx={{ marginTop: '12px' }}>
              <CharityCard charityId={initiative?.charityId ?? ''} variant="link" />
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ height: 'auto' }}
              fullWidth={true}
              onClick={() => {
                navigateToInitiative(initiative);
              }}
            >
              Learn More
            </Button>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ paddingTop: '24px' }}>
          <Divider />
        </Grid>
      </Grid>
    );
  };

  const renderQuestions = () => {
    return (
      <Grid size={{ xs: 12 }} container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="body1" component="div" sx={{ fontWeight: 'bold' }}>
            {questions[questionIndex].questionText}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }} container spacing={2}>
          {questions[questionIndex].answerAImageUrl && questions[questionIndex].answerAImageUrl ? (
            <Grid size={{ xs: 12 }} container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: '24px',
                    backgroundColor:
                      answers.length >= questionIndex && answers[questionIndex] == 'A'
                        ? 'rgb(235, 250, 255)'
                        : '#ffffff',
                  }}
                >
                  <Grid size={{ xs: 12 }} container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <img
                        alt="Answer A Image"
                        src={questions[questionIndex].answerAImageUrl}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          borderRadius: '24px',
                          objectFit: 'cover',
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ height: 'auto' }}
                        fullWidth={true}
                        onClick={() => {
                          let updatedAnswer = answers.slice();
                          updatedAnswer[questionIndex] = 'A';
                          setAnswers(updatedAnswer);
                        }}
                      >
                        {questions[questionIndex].answerAText}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 6 }}>
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: '24px',
                    backgroundColor:
                      answers.length >= questionIndex && answers[questionIndex] == 'B'
                        ? 'rgb(235, 250, 255)'
                        : '#ffffff',
                  }}
                >
                  <Grid size={{ xs: 12 }} container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <img
                        alt="Answer B Image"
                        src={questions[questionIndex].answerBImageUrl}
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          borderRadius: '24px',
                          objectFit: 'cover',
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ height: 'auto' }}
                        fullWidth={true}
                        onClick={() => {
                          let updatedAnswer = answers.slice();
                          updatedAnswer[questionIndex] = 'B';
                          setAnswers(updatedAnswer);
                        }}
                      >
                        {questions[questionIndex].answerBText}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <RadioGroup
              value={answers.length >= questionIndex ? answers[questionIndex] : ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                let updatedAnswer = answers.slice();
                updatedAnswer[questionIndex] = event.target.value;
                setAnswers(updatedAnswer);
              }}
            >
              <FormControlLabel
                value="A"
                control={<Radio />}
                label={questions[questionIndex].answerAText}
              />
              <FormControlLabel
                value="B"
                control={<Radio />}
                label={questions[questionIndex].answerBText}
              />
            </RadioGroup>
          )}
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ paddingTop: '12px', paddingBottom: '12px' }}>
          <Divider />
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog
      open={true}
      fullScreen={fullScreen}
      onClose={() => {
        onClose();
      }}
    >
      <IconButton
        aria-label="close"
        onClick={() => {
          onClose();
        }}
        sx={(theme) => ({
          position: 'absolute',
          right: 12,
          top: 24,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle sx={{ p: 2 }}>
        {initiatives.length == 0
          ? `What Change Inspires You? (${questionIndex + 1}/${questionMax})`
          : 'Discover projects that reflect the impact you care about most.'}
      </DialogTitle>
      <DialogContent>
        {busy ? (
          <LinearProgress />
        ) : (
          <React.Fragment>
            {questions.length > 0 && initiatives.length == 0 && renderQuestions()}
            {initiatives.length > 0 && (
              <Grid size={{ xs: 12 }} container spacing={2}>
                {initiatives.map((initiative) => {
                  return renderInitiative(initiative);
                })}
              </Grid>
            )}
            <Grid size={{ xs: 12 }} container spacing={2}>
              {initiatives.length > 0 && (
                <Grid size={{ xs: 6 }}>
                  <Button
                    variant="outlined"
                    fullWidth={true}
                    onClick={async () => {
                      setBusy(true);
                      setQuestionIndex(-1);
                      setQuestionMax(3);
                      setQuestions([]);
                      setAnswers([]);
                      setInitiatives([]);
                      await handleGetNextQuestion(3, -1, [], []);
                      setBusy(false);
                    }}
                  >
                    Start Over
                  </Button>
                </Grid>
              )}
              {initiatives.length > 0 && (
                <Grid size={{ xs: 6 }}>
                  <Button
                    variant="outlined"
                    fullWidth={true}
                    onClick={async () => {
                      setBusy(true);
                      setQuestionMax(questionMax + 2);
                      setInitiatives([]);
                      await handleGetNextQuestion(
                        questionMax + 2,
                        questionIndex,
                        questions,
                        answers,
                      );
                    }}
                  >
                    Keep Refining
                  </Button>
                </Grid>
              )}
              {initiatives.length == 0 && (
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="outlined"
                    fullWidth={true}
                    disabled={answers.length == questionIndex || answers[questionIndex].length == 0}
                    onClick={async () => {
                      await handleGetNextQuestion(questionMax, questionIndex, questions, answers);
                    }}
                  >
                    Next
                  </Button>
                </Grid>
              )}
            </Grid>
          </React.Fragment>
        )}
      </DialogContent>
    </Dialog>
  );
}
